import { useAuth } from "../context/auth";
import { TypeUser } from "../interface/enum/TypeUser";
import React from "react";

// تعریف interface برای دسترسی‌های منو
export interface MenuPermission {
  menuId: string;
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
}

// تعریف interface برای کاربر با دسترسی‌ها
export interface UserWithPermissions {
  id: string;
  userName: string;
  email: string;
  roleName: string;
  permissions?: MenuPermission[];
}

// تابع کمکی برای لاگ کردن دسترسی‌ها
const logAccessAttempt = (
  userName: string | null | undefined,
  menuId: string,
  permission: string,
  hasAccess: boolean
) => {
  // فعلاً لاگ غیرفعال است
  // if (!hasAccess && userName) {
  //   console.log(
  //     `🔒 ACCESS DENIED - User ${userName} tried to access ${menuId} with ${permission} permission`
  //   );
  // }
};

// Hook برای بررسی دسترسی‌های کاربر
export const usePermissions = () => {
  const { user } = useAuth();

  // بررسی اینکه آیا کاربر مدیر است
  const isAdmin = (): boolean => {
    return user?.roleName === TypeUser.ADMIN || user?.userName === "HFatahi73";
  };

  // بررسی اینکه آیا کاربر کارمند است
  const isEditor = (): boolean => {
    return user?.roleName === TypeUser.EDITOR;
  };

  // بررسی اینکه آیا کاربر مشتری است
  const isCustomer = (): boolean => {
    return user?.roleName === TypeUser.CUSTOMER;
  };

  // بررسی دسترسی به منوی خاص
  const hasMenuAccess = (
    menuId: string,
    permission: "view" | "create" | "edit" | "delete" = "view"
  ): boolean => {
    // مدیران همه دسترسی‌ها را دارند
    if (isAdmin()) {
      return true;
    }

    // اگر کاربر دسترسی‌های تعریف شده‌ای نداشته باشد، بر اساس نقش تصمیم‌گیری می‌کنیم
    if (!user?.permissions || user.permissions.length === 0) {
      const hasAccess = hasDefaultAccess(menuId, permission);
      logAccessAttempt(user?.userName, menuId, permission, hasAccess);
      return hasAccess;
    }

    // بررسی دسترسی‌های تعریف شده
    const menuPermission = user.permissions.find((p) => p.menuId === menuId);
    if (!menuPermission) {
      const hasAccess = hasDefaultAccess(menuId, permission);
      logAccessAttempt(user?.userName, menuId, permission, hasAccess);
      return hasAccess;
    }

    const hasAccess = menuPermission[permission] || false;
    logAccessAttempt(user?.userName, menuId, permission, hasAccess);
    return hasAccess;
  };

  // دسترسی‌های پیش‌فرض بر اساس نقش
  const hasDefaultAccess = (
    menuId: string,
    permission: "view" | "create" | "edit" | "delete"
  ): boolean => {
    if (isAdmin()) return true;

    // دسترسی‌های پیش‌فرض برای کارمندان
    if (isEditor()) {
      const editorAccess: { [key: string]: { [key: string]: boolean } } = {
        "my-tasks": { view: true, create: true, edit: true, delete: false },
        tasks: { view: true, create: true, edit: true, delete: false },
        customers: { view: false, create: false, edit: false, delete: false }, // محدود شده
        employees: { view: true, create: false, edit: true, delete: false }, // اجازه مشاهده و ویرایش
        agreements: { view: true, create: true, edit: true, delete: false },
        services: { view: true, create: false, edit: false, delete: false },
        transactions: { view: true, create: false, edit: false, delete: false },
        followups: { view: true, create: true, edit: true, delete: false },
        reports: { view: false, create: false, edit: false, delete: false }, // محدود شده
        settings: { view: false, create: false, edit: false, delete: false },
        "base-definitions": {
          view: false,
          create: false,
          edit: false,
          delete: false,
        },
        duties: { view: true, create: true, edit: true, delete: false },
        "sales-funnel": { view: true, create: true, edit: true, delete: false },
      };

      const result = editorAccess[menuId]?.[permission] || false;
      return result;
    }

    // دسترسی‌های پیش‌فرض برای مشتریان
    if (isCustomer()) {
      const customerAccess: { [key: string]: { [key: string]: boolean } } = {
        "my-tasks": { view: true, create: false, edit: false, delete: false },
        tasks: { view: false, create: false, edit: false, delete: false },
        customers: { view: false, create: false, edit: false, delete: false },
        employees: { view: false, create: false, edit: false, delete: false },
        agreements: { view: true, create: false, edit: false, delete: false },
        services: { view: true, create: false, edit: false, delete: false },
        transactions: { view: true, create: false, edit: false, delete: false },
        followups: { view: true, create: false, edit: false, delete: false },
        reports: { view: false, create: false, edit: false, delete: false },
        settings: { view: false, create: false, edit: false, delete: false },
        "base-definitions": {
          view: false,
          create: false,
          edit: false,
          delete: false,
        },
        duties: { view: false, create: false, edit: false, delete: false },
        "sales-funnel": {
          view: false,
          create: false,
          edit: false,
          delete: false,
        },
      };

      return customerAccess[menuId]?.[permission] || false;
    }

    return false;
  };

  // بررسی دسترسی به منوی خاص
  const canView = (menuId: string): boolean => hasMenuAccess(menuId, "view");
  const canCreate = (menuId: string): boolean =>
    hasMenuAccess(menuId, "create");
  const canEdit = (menuId: string): boolean => hasMenuAccess(menuId, "edit");
  const canDelete = (menuId: string): boolean =>
    hasMenuAccess(menuId, "delete");

  // بررسی دسترسی به عملیات‌های مختلف
  const canManageUsers = (): boolean => {
    return isAdmin() || (isEditor() && canView("employees"));
  };

  const canManageCustomers = (): boolean => {
    return isAdmin() || (isEditor() && canView("customers"));
  };

  const canManageTasks = (): boolean => {
    return isAdmin() || (isEditor() && canView("tasks"));
  };

  const canManageAgreements = (): boolean => {
    return isAdmin() || (isEditor() && canView("agreements"));
  };

  const canManageServices = (): boolean => {
    return isAdmin() || (isEditor() && canView("services"));
  };

  const canViewReports = (): boolean => {
    return isAdmin() || (isEditor() && canView("reports"));
  };

  const canManageSettings = (): boolean => {
    return isAdmin() || (isEditor() && canView("settings"));
  };

  const canManageBaseDefinitions = (): boolean => {
    return isAdmin() || (isEditor() && canView("base-definitions"));
  };

  const canManageDuties = (): boolean => {
    return isAdmin() || (isEditor() && canView("duties"));
  };

  const canManageSalesFunnel = (): boolean => {
    return isAdmin() || (isEditor() && canView("sales-funnel"));
  };

  return {
    isAdmin,
    isEditor,
    isCustomer,
    hasMenuAccess,
    canView,
    canCreate,
    canEdit,
    canDelete,
    canManageUsers,
    canManageCustomers,
    canManageTasks,
    canManageAgreements,
    canManageServices,
    canViewReports,
    canManageSettings,
    canManageBaseDefinitions,
    canManageDuties,
    canManageSalesFunnel,
  };
};

// Component برای نمایش شرطی بر اساس دسترسی
export const PermissionGuard = ({
  children,
  menuId,
  permission = "view",
  fallback = null,
}: {
  children: React.ReactNode;
  menuId: string;
  permission?: "view" | "create" | "edit" | "delete";
  fallback?: React.ReactNode;
}): React.ReactElement | null => {
  const { hasMenuAccess } = usePermissions();

  if (!hasMenuAccess(menuId, permission)) {
    return fallback as React.ReactElement | null;
  }

  return children as React.ReactElement | null;
};

// Component برای نمایش شرطی دکمه‌های عملیات
export const ActionPermissionGuard = ({
  children,
  menuId,
  permission,
  fallback = null,
}: {
  children: React.ReactNode;
  menuId: string;
  permission: "create" | "edit" | "delete";
  fallback?: React.ReactNode;
}): React.ReactElement | null => {
  const { hasMenuAccess } = usePermissions();

  if (!hasMenuAccess(menuId, permission)) {
    return fallback as React.ReactElement | null;
  }

  return children as React.ReactElement | null;
};

// Hook برای فیلتر کردن منوها بر اساس دسترسی
export const useFilteredNavigation = (navigation: any[]) => {
  const { canView } = usePermissions();

  const filterNavigation = (items: any[]): any[] => {
    return items.filter((item) => {
      // اگر header یا divider است، نمایش بده
      if (item.kind === "header" || item.kind === "divider") {
        return true;
      }

      // بررسی دسترسی به منو
      if (item.segment) {
        const menuId = getMenuIdFromSegment(item.segment);

        if (!canView(menuId)) {
          return false;
        }
      }

      // بررسی زیرمنوها
      if (item.children) {
        const filteredChildren = filterNavigation(item.children);
        if (filteredChildren.length === 0) {
          return false;
        }
        item.children = filteredChildren;
      }

      return true;
    });
  };

  return filterNavigation(navigation);
};

// Hook برای فیلتر کردن عملیات‌های جدول
export const useFilteredActions = (menuId: string) => {
  const { canCreate, canEdit, canDelete } = usePermissions();

  const filterActions = (actions: any[]): any[] => {
    return actions.filter((action) => {
      // اگر action یک JSX element است و دارای data-permission attribute است
      if (
        React.isValidElement(action) &&
        (action.props as any)["data-permission"]
      ) {
        const permission = (action.props as any)["data-permission"];
        switch (permission) {
          case "create":
            return canCreate(menuId);
          case "edit":
            return canEdit(menuId);
          case "delete":
            return canDelete(menuId);
          default:
            return true;
        }
      }

      // اگر action یک تابع است که بر اساس دسترسی تصمیم‌گیری می‌کند
      if (typeof action === "function") {
        return action();
      }

      return true;
    });
  };

  return filterActions;
};

// تابع کمکی برای تبدیل segment به menuId
const getMenuIdFromSegment = (segment: string): string => {
  const segmentToMenuId: { [key: string]: string } = {
    works: "tasks",
    "users/customer": "customers",
    "users/editor": "employees",
    tasks: "tasks",
    "followups-schedule": "followups",
    agreements: "agreements",
    services: "services",
    transactions: "transactions",
    "sales-funnel": "sales-funnel",
    base: "base-definitions",
    setting: "settings",
    "followup-groups": "followups",
    duties: "duties",
  };

  return segmentToMenuId[segment] || segment;
};
